import { makeStyles } from '@material-ui/core/styles';
import Toolbar from '@material-ui/core/Toolbar';
import ViewListIcon from '@material-ui/icons/ViewListTwoTone';
import ViewModuleIcon from '@material-ui/icons/ViewModuleTwoTone';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import { QueryOrderSettings } from '@sprinx/query-builder';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import clsx from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import SelectFieldItem from '@sprinx/react-mui-fields/SelectFieldItem';
import SelectField from '@sprinx/react-mui-fields/SelectField';
import kebabCase from 'lodash/kebabCase';
import { useHistory } from 'react-router-dom';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { currencyState, localeState } from '../../../api/appState';
import { customerPriceGroupState } from '../../../api/customer/customer';
import { CatalogueView, catalogueFilterState } from '../../../api/products/catalogue';
import { Manufacturer } from '../../../api/products/productManufacturers';
import { mainNavigationMatchState } from '../../../api/mainNavigation';
import { activeManufacturersFilter } from '../../../config';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';
import { useAdminConfig } from '../../../adminConfig';

export type CataloguePageListToolbarProps = StandardDivProps<CataloguePageListToolbarClassKey> & {
  catalogueOrder: QueryOrderSettings;
  catalogueView: CatalogueView;
  onCatalogueOrderChange: (nextOrder: QueryOrderSettings) => void;
  onCatalogueViewChange: (nextView: CatalogueView) => void;
};

export type CataloguePageListToolbarClassKey = 'root';

const themeSettings = { name: 'CataloguePageListToolbar' };
const useStyles = makeStyles(
  (theme) => ({
    root: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0, 1),
      },
      '@media(max-width:450px)': {
        flexDirection: 'column',
        alignItems: 'stretch',
      },
      gap: '0.5rem',
      // '&..MuiToggleButtonGroup-groupedHorizontal:not(:first-child)': {
      //   border: '1px solid #0C2F59',
      // },
    },
    ordering: {
      flexGrow: 1,
      margin: theme.spacing(0.5, 0),
      '& button': {
        fontSize: '16px',
        textTransform: 'none',
      },
    },
    viewActions: {
      margin: theme.spacing(0.5, 0, 0.5, 0.5),
      '@media(max-width:450px)': {
        alignSelf: 'flex-end',
      },
    },
    highligtedButton: {
      color: '#000',
      border: '1px solid transparent',

      '&.Mui-selected': {
        backgroundColor: 'transparent',
        color: '#042751',
        border: '1px solid #042751',
        textDecoration: 'none',
      },

      '&:hover': {
        border: '1px solid #042751',
      },
    },
  }),
  themeSettings,
);

const CataloguePageListToolbar = React.forwardRef<HTMLDivElement, CataloguePageListToolbarProps>(
  (
    { className, classes: pClasses, catalogueView, catalogueOrder, onCatalogueOrderChange, onCatalogueViewChange },
    ref,
  ) => {
    const history = useHistory();
    const classes = useStyles({ classes: pClasses });
    const t = useTranslateWithFallback();
    const OrderByMap = useOrderByMap();
    const defaultManufacturer: Manufacturer = useMemo(
      () => ({ name: t('cataloguePage/chooseManufacturer'), taxonomies: [], taxonomy: '_default' }),
      [t],
    );
    const [selectedManufacturer, setSelectedManufacturer] = useState<string>(() => {
      if (typeof window === 'undefined') return defaultManufacturer.name;

      const nextUrl = new URL(window.location.href);
      const manufacturer = nextUrl.searchParams.get('manufacturer');
      if (!manufacturer) return defaultManufacturer.name;

      return manufacturer;
    });
    const [category, setCategory] = useState<string | undefined>(undefined);
    const mainNavigationMatchStateValue = useRecoilValue(mainNavigationMatchState);
    const isNovinky = mainNavigationMatchStateValue.url?.includes('/catalogue/novinky');
    const [filter, setFilter] = useRecoilState(catalogueFilterState);
    const isOrderActive = (key: OrderKey) => JSON.stringify(catalogueOrder) === JSON.stringify(OrderByMap[key]);
    const isPriceAsc =
      catalogueOrder.orderBy === OrderByMap.priceAsc.orderBy && catalogueOrder.orderDirection === 'asc';
    const isNewest = catalogueOrder.orderBy === 'lastWarehouseReceipt' && catalogueOrder.orderDirection === 'desc';
    const isPriceActive = catalogueOrder.orderBy === OrderByMap.priceAsc.orderBy;
    const isReceiptActive = catalogueOrder.orderBy === 'lastWarehouseReceipt';
    const togglePriceSort = () => {
      if (isPriceAsc) {
        handleSortClick('priceDesc');
      } else {
        handleSortClick('priceAsc');
      }
    };

    const toggleReceiptSort = () => {
      if (isNewest) {
        handleSortClick('oldestReceipt');
      } else {
        handleSortClick('newestReceipt');
      }
    };

    const handleCatalogueViewChange = (v: CatalogueView) => {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem('catalogueView', v);
      }
      onCatalogueViewChange(v);
    };

    const handleManufacturerChange = (manufacturer: string | undefined) => {
      setSelectedManufacturer(manufacturer || defaultManufacturer.name);
      setCategory(mainNavigationMatchStateValue.params.categories);
      setFilter((prev) => {
        const extraTaxonomies =
          manufacturer !== defaultManufacturer.name
            ? [`/manufacturer/${kebabCase(manufacturer)}`]
            : prev.extraTaxonomies;

        return {
          ...prev,
          extraTaxonomies,
        };
      });
    };
    const { manufacturers: rawManufacturers } = useAdminConfig();

    const manufacturers = useMemo(() => {
      if (activeManufacturersFilter !== true) {
        return [defaultManufacturer];
      }
      if (!rawManufacturers) return [defaultManufacturer];
      return [
        defaultManufacturer,
        ...rawManufacturers.filter((m) => m.taxonomies.includes(mainNavigationMatchStateValue.url.slice(3))),
      ];
    }, [defaultManufacturer, mainNavigationMatchStateValue.url, rawManufacturers]);

    useEffect(() => {
      if (mainNavigationMatchStateValue.params.categories !== category) {
        setSelectedManufacturer(defaultManufacturer.name);
        setCategory(mainNavigationMatchStateValue.params.categories);
      } else if (
        selectedManufacturer === defaultManufacturer.name &&
        filter.extraTaxonomies &&
        filter.extraTaxonomies.length > 0
      ) {
        const filteredManufacturer = manufacturers.find((mt) => (filter.extraTaxonomies || []).includes(mt.taxonomy));
        if (filteredManufacturer) {
          setSelectedManufacturer(filteredManufacturer.name);
        }
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      defaultManufacturer.name,
      mainNavigationMatchStateValue.params.categories,
      filter.extraTaxonomies,
      manufacturers,
    ]);

    useEffect(() => {
      const nextUrl = new URL(window.location.href);
      if (selectedManufacturer === defaultManufacturer.name) {
        nextUrl.searchParams.delete('manufacturer');
      } else {
        nextUrl.searchParams.set('manufacturer', selectedManufacturer);
      }
      const nextPath = nextUrl.pathname + nextUrl.search;

      history.push(nextPath);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedManufacturer]);

    useEffect(() => {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('manufacturer')) return;

      const manufacturer = url.searchParams.get('manufacturer');
      handleManufacturerChange(manufacturer || defaultManufacturer.name);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    type OrderKey = 'nameAsc' | 'skuAsc' | 'priceAsc' | 'priceDesc' | 'newestReceipt' | 'oldestReceipt';

    const handleSortClick = (key: OrderKey) => {
      const nextOrder = OrderByMap[key];
      if (!nextOrder) return;

      onCatalogueOrderChange(nextOrder);

      if (typeof window !== 'undefined') {
        const nextUrl = new URL(window.location.href);

        nextUrl.searchParams.set('orderBy', nextOrder.orderBy);
        nextUrl.searchParams.set('orderDirection', nextOrder.orderDirection);
        nextUrl.searchParams.delete('page');

        history.replace(nextUrl.pathname + nextUrl.search);
      }
    };

    return (
      <Toolbar ref={ref} className={clsx(classes.root, className)}>
        <div className={classes.ordering}>
          <ToggleButtonGroup exclusive size='small'>
            <ToggleButton
              onClick={() => handleSortClick('nameAsc')}
              className={classes.highligtedButton}
              value={JSON.stringify(OrderByMap['nameAsc'])}
              selected={isOrderActive('nameAsc')}
              aria-label='order by name'
            >
              {t('cataloguePage/orderBy/name')}
            </ToggleButton>
            <ToggleButton
              onClick={() => handleSortClick('skuAsc')}
              className={classes.highligtedButton}
              value={JSON.stringify(OrderByMap['skuAsc'])}
              selected={isOrderActive('skuAsc')}
              aria-label='podle kódu'
            >
              {t('cataloguePage/orderBy/code')}
            </ToggleButton>
            <ToggleButton
              onClick={togglePriceSort}
              className={classes.highligtedButton}
              value='priceToggle'
              selected={isPriceActive}
              aria-label='price toggle'
            >
              Cena&nbsp;
              {isPriceAsc ? <ArrowUpwardIcon fontSize='small' /> : <ArrowDownwardIcon fontSize='small' />}
            </ToggleButton>
            {isNovinky && (
              <>
                {isNovinky && (
                  <ToggleButton
                    onClick={toggleReceiptSort}
                    className={classes.highligtedButton}
                    value='receiptToggle'
                    selected={isReceiptActive}
                    aria-label='receipt toggle'
                  >
                    Datum přidání&nbsp;
                    {isNewest ? <ArrowDownwardIcon fontSize='small' /> : <ArrowUpwardIcon fontSize='small' />}
                  </ToggleButton>
                )}
              </>
            )}
          </ToggleButtonGroup>
        </div>
        {activeManufacturersFilter && (
          <div>
            <SelectField<string>
              ref={ref}
              fullWidth
              value={selectedManufacturer}
              className={classes.root}
              onChange={handleManufacturerChange}
            >
              {manufacturers.map((m, idx) => (
                <SelectFieldItem key={idx} value={m.name}>
                  {m.name}
                </SelectFieldItem>
              ))}
            </SelectField>
          </div>
        )}
        <div className={classes.viewActions}>
          <ToggleButtonGroup
            value={catalogueView}
            onChange={(evnt: any, v: CatalogueView) => handleCatalogueViewChange(v)}
            exclusive
            size='small'
          >
            <ToggleButton value='row' aria-label='row' disabled={catalogueView === 'row'}>
              <ViewListIcon />
            </ToggleButton>
            <ToggleButton value='card' aria-label='card' disabled={catalogueView === 'card'}>
              <ViewModuleIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
      </Toolbar>
    );
  },
);

CataloguePageListToolbar.displayName = 'CataloguePageListToolbar';

export default withThemeProps(themeSettings)(CataloguePageListToolbar);

const orderMultilang = (params: { field: string; locale: string }) => {
  const { field, locale } = params;
  return [field, locale].join('.');
};
const orderMultiprice = (params: { currency: string; field: string; prefixField?: string; priceGroup?: string }) => {
  const { currency, field, prefixField = '_pricing', priceGroup = '_default' } = params;
  return [prefixField, priceGroup, field, currency].join('.');
};

const useOrderByMap = (): Record<
  /* 'recommended' | */ 'nameAsc' | 'skuAsc' | 'priceAsc' | 'priceDesc' | 'newestReceipt' | 'oldestReceipt',
  QueryOrderSettings
> => {
  const locale = useRecoilValue(localeState);
  const currency = useRecoilValue(currencyState);
  const priceGroup = useRecoilValue(customerPriceGroupState);

  return {
    // recommended: {
    //   orderBy: '_ordering._default',
    //   orderDirection: 'asc',
    // },
    nameAsc: {
      orderBy: orderMultilang({ field: 'displayName', locale }),
      orderDirection: 'asc',
    },
    skuAsc: {
      orderBy: 'sku',
      orderDirection: 'asc',
    },
    priceAsc: {
      orderBy: orderMultiprice({ field: 'price', currency, priceGroup }),
      orderDirection: 'asc',
    },
    priceDesc: {
      orderBy: orderMultiprice({ field: 'price', currency, priceGroup }),
      orderDirection: 'desc',
    },
    newestReceipt: {
      orderBy: 'lastWarehouseReceipt',
      orderDirection: 'desc',
    },
    oldestReceipt: {
      orderBy: 'lastWarehouseReceipt',
      orderDirection: 'asc',
    },
  };
};
